
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Review } from "@/types/review";
import { useLanguage } from "@/context/LanguageContext";
import { ChevronLeft } from "lucide-react";
import ReviewMap from "@/components/reviews/ReviewMap";
import { Separator } from "@/components/ui/separator";
import { AddressHeader } from "@/components/reviews/detail/AddressHeader";
import SingleReviewCard from "@/components/reviews/SingleReviewCard";

const ReviewDetail = () => {
  const { id } = useParams();
  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();
  const [sameAddressReviews, setSameAddressReviews] = useState<Review[]>([]);

  const normalizeAddress = (address: string) =>
    address?.trim().replace(/\s+/g, '').replace(/[,.，。]/g, '').toLowerCase() || '';

  // Transform database row to Review type
  const transformReview = (data: any): Review => {
    return {
      ...data,
      deposit_type: data.deposit_type as "amount" | "months",
      coordinates: data.coordinates as { lat: number; lng: number },
      rental_status: data.rental_status as "viewed" | "renting" | "ended",
      housing_type: data.housing_type as "basement" | "house" | "room" | "condo" | "apartment" | "semi-detached",
      utilities_included: data.utilities_included || [],
      bedrooms: data.bedrooms || 0,
      living_rooms: data.living_rooms || 0,
      bathrooms: data.bathrooms || 0,
      pet_allowed: data.pet_allowed || false,
      image_urls: data.image_urls || [],
      parking_information: data.parking_information || [],
      // Ensure all required fields have proper defaults
      rating: data.rating || 0,
      rent: data.rent || "0",
      deposit_value: data.deposit_value || "0",
      includes_utilities: data.includes_utilities || false,
      immigrant_friendly: data.immigrant_friendly || false,
      accepts_overseas_proof_of_funds: data.accepts_overseas_proof_of_funds || false,
      accepts_overseas_employment_proof: data.accepts_overseas_employment_proof || false,
      pet_fee: data.pet_fee || false,
      no_pet_limit_count: data.no_pet_limit_count || false,
      no_pet_limit_type: data.no_pet_limit_type || false,
      requires_employment_proof: data.requires_employment_proof || false,
      requires_credit_check: data.requires_credit_check || false,
      has_income_requirement: data.has_income_requirement || false,
      requires_financial_proof: data.requires_financial_proof || false,
      allows_daily_rental: data.allows_daily_rental || false,
      allows_monthly_rental: data.allows_monthly_rental || false,
      allows_semiannual_rental: data.allows_semiannual_rental || false,
      allows_contract_termination: data.allows_contract_termination || false,
      allows_contract_transfer: data.allows_contract_transfer || false,
      viewing_during_lease: data.viewing_during_lease || false,
      required_documents: data.required_documents || [],
      no_proof_required: data.no_proof_required || false
    };
  };

  useEffect(() => {
    setReview(null);
    const fetchReview = async () => {
      setLoading(true);
      try {
        console.log("Fetching review with ID:", id);
        
        const { data, error } = await supabase
          .from('reviews')
          .select('*')
          .eq('id', id)
          .single();

        if (error) {
          console.error("Error fetching review:", error);
          throw error;
        }
        
        if (!data) {
          console.log("No review data found");
          setReview(null);
          return;
        }
        
        console.log("Raw review data:", data);
        const transformedReview = transformReview(data);
        console.log("Transformed review:", transformedReview);
        
        setReview(transformedReview);
      } catch (error) {
        console.error("Error fetching review:", error);
        setReview(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchReview();
    } else {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setSameAddressReviews([]);
    const fetchSameAddressReviews = async () => {
      if (!review?.address_detail) {
        console.log("No address_detail available for fetching same address reviews");
        return;
      }
      
      try {
        console.log("Fetching same address reviews for:", review.address_detail);
        
        const { data, error } = await supabase
          .from('reviews')
          .select('*');
          
        if (error) {
          console.error("Error fetching same address reviews:", error);
          return;
        }
        
        if (!data) {
          console.log("No reviews data found");
          return;
        }
        
        const filtered = data.filter(r => 
          r && 
          r.address_detail && 
          typeof r.rating === 'number' && 
          normalizeAddress(r.address_detail) === normalizeAddress(review.address_detail) && 
          r.id !== review.id
        );
        
        console.log("Filtered same address reviews:", filtered);
        
        const transformedReviews = filtered.map(transformReview);
        setSameAddressReviews(transformedReviews);
      } catch (error) {
        console.error("Error in fetchSameAddressReviews:", error);
      }
    };
    
    if (review) {
      fetchSameAddressReviews();
    }
  }, [review]);

  const allReviews = review ? [review, ...sameAddressReviews].filter(r => r && typeof r.rating === 'number') : [];
  const averageRating = allReviews.length > 0
    ? allReviews.reduce((sum, r) => sum + r.rating, 0) / allReviews.length
    : 0;
    
  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${year}/${month}/${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return '';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="container py-8">
          <div className="flex flex-col gap-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-96" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!review) {
    return (
      <MainLayout>
        <div className="container py-8">
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <h1 className="text-2xl font-bold">{t('reviews.noReviewsFound')}</h1>
            <p className="text-muted-foreground">
              {t('noReviewsForAddress')}
            </p>
            <Button asChild>
              <Link to="/reviews">{t('backToReviews')}</Link>
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Get display title (community or landlord name if available, otherwise address)
  const displayTitle = review.community_name || review.landlord_name || review.address_detail || '';
  const latestReviewDate = review.created_at ? formatDate(review.created_at) : '';
  const latestReviewText = language === 'zh-TW' ? `最新評論：${latestReviewDate}` : `Latest Review: ${latestReviewDate}`;

  console.log("Rendering ReviewDetail with:", { review, sameAddressReviews, allReviews });

  return (
    <MainLayout>
      <div className="container py-8">
        <div className="mb-6">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/reviews" className="flex items-center gap-2">
              <ChevronLeft className="h-4 w-4" />
              {t('backToReviews')}
            </Link>
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <AddressHeader 
              address={review.address_detail || ''}
              averageRating={averageRating}
              totalReviews={allReviews.length}
              rentalYearsRange={latestReviewText}
              displayTitle={displayTitle}
            />
            
            <div className="h-60 border border-border rounded-lg overflow-hidden shadow-sm relative z-10">
              <ReviewMap reviews={allReviews} height="100%" clickable={false} />
            </div>
            
            <Separator className="my-6" />
            
            {/* Display the main review using SingleReviewCard */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">
                {language === 'zh-TW' ? '詳細評價' : 'Detailed Review'}
              </h2>
              
              <SingleReviewCard review={review} />
              
              {/* Show other reviews at the same address if any */}
              {sameAddressReviews.length > 0 && (
                <>
                  <Separator className="my-6" />
                  <h3 className="text-lg font-semibold">
                    {language === 'zh-TW' ? '同地址其他評價' : 'Other Reviews at This Address'}
                  </h3>
                  <div className="space-y-4">
                    {sameAddressReviews.map(sameAddressReview => (
                      <SingleReviewCard key={sameAddressReview.id} review={sameAddressReview} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          
          <div className="space-y-6">
            {/* Sidebar content can be added here if needed */}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ReviewDetail;
